import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getBrands } from '../../api/getBrands/getBrands';
import styles from './Brand.module.scss';
import {
  BreadcrumbHeading,
  DetailsPanel,
  EditIcon,
  FormatInformation,
  IconButton,
  Spinner,
  useSnackbar,
  SnackbarStatus,
} from '@weareopenr/component-library';
import { getLogo } from 'src/utils/getLogo';
import BrandModal from 'src/components/BrandModal';
import { usePermissions, useUserTracking } from 'src/hooks';
import { ExtensionsList } from 'src/components/Extensions';

export const Brand = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const { showSnackbar } = useSnackbar();
  const [isBrandModalOpen, setIsBrandModalOpen] = useState<boolean>(false);
  const { canEditBrands } = usePermissions();
  const { trackEvent } = useUserTracking();

  const handleBrandModalToggle = (): void => {
    if (canEditBrands) {
      setIsBrandModalOpen((prev) => {
        if (!prev) {
          trackEvent('brand_edit_modal_opened', { brand_id: brandId });
        }
        return !prev;
      });
    }
  };

  const { data, refetch } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    onError: () =>
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst fetching brands',
      }),
  });

  const currentBrand = data?.find((brand) => brand?.brand_id === brandId);

  return (
    <>
      {currentBrand ? (
        <>
          <BreadcrumbHeading
            breadcrumbs={[
              { label: 'Brands', path: '/brands' },
              { label: currentBrand.name, path: '' },
            ]}
          />
          <div className={styles['details-container']}>
            <DetailsPanel
              title="Details"
              action={
                canEditBrands ? (
                  <IconButton
                    onClick={handleBrandModalToggle}
                    aria-label="Edit brand details"
                    icon={EditIcon}
                  />
                ) : undefined
              }
            >
              <DetailsPanel.Row>
                <DetailsPanel.Column>
                  <FormatInformation heading="Name">{currentBrand.name}</FormatInformation>
                  <FormatInformation heading="Venues">
                    {currentBrand.venues > 0 ? String(currentBrand.venues) : 'No Venues'}
                  </FormatInformation>
                </DetailsPanel.Column>
                <DetailsPanel.Column>
                  <img
                    className={styles['logo-image']}
                    src={getLogo(currentBrand?.logo_url, '')}
                    alt={currentBrand.name}
                  />
                </DetailsPanel.Column>
              </DetailsPanel.Row>
            </DetailsPanel>
            {brandId && (
              <DetailsPanel title="Extensions">
                <ExtensionsList brandId={brandId} />
              </DetailsPanel>
            )}
          </div>

          {canEditBrands && (
            <BrandModal
              open={isBrandModalOpen}
              closeModal={handleBrandModalToggle}
              refetch={refetch}
              brands={data}
              variant="update"
              currentBrand={currentBrand}
            />
          )}
        </>
      ) : (
        <div className={styles['spinner']}>
          <Spinner size="xlarge" colour="rainbow" />
        </div>
      )}
    </>
  );
};
